import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import { bindActionCreators } from 'redux';

import Button from 'components/form/Button.component';
import { SERVICE_DESCRIPTION_IMAGES } from 'constants/images/onlineBooking.const';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectSalonServicesCategories } from 'selectors/salon.selectors';
import { selectBookingCategoryServices, selectBookingIsSettingsDataFetched } from 'selectors/settings.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';

import { getCategories, putService } from 'onboarding/services/actions';
import { getSalonOnlineSettings } from 'settings/online-booking/actions';
import AddEditDescriptionModal from 'settings/page-elements/AddEditServiceDescriptionModal';

import CN from './ServicesDescription.module.scss';

const L = logger('ServicesDescription'); // eslint-disable-line no-unused-vars

const ServicesDescription = ({ categoryServices, categories, putService }) => {
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();
  const serviceDescriptionImage = useCountryImage(SERVICE_DESCRIPTION_IMAGES);

  const { val: isModalRendered, yay: showModal, nay: hideModal } = useTip(false);

  const handleSaveService = useCallback(
    (service) => {
      if (isNotFullArray(categories)) return;

      for (const category of categories) {
        const serviceChanged = category.services.find((s) => fromAnyToString(s.id) === fromAnyToString(service.id));
        if (serviceChanged) {
          serviceChanged.description = service.description;
          putService(serviceChanged, salonId);
          break;
        }
      }
    },
    [categories, putService, salonId],
  );

  return (
    <div data-bem="ServiceDescription">
      <div className={CN.container}>
        <div>
          <div className={CN.text}>{t('onlineBooking.generalSettings.serviceDescription.description')}</div>
          <div className={CN.negativeGap}>
            <Button variant="primary" onClick={showModal}>
              <span data-bem="ServiceDescription__setup-edit" className={CN.buttonSpace}>
                {isFullArray(categoryServices) ? t('onlineBooking.generalSettings.serviceDescription.editButton') : t('common.buttons.setupUpp')}
              </span>
            </Button>
          </div>
        </div>
        <div className={CN.imageContainer}>
          <img src={serviceDescriptionImage} alt="Description  illustration" className={CN.imgDescription} />
        </div>
      </div>

      {isModalRendered && <AddEditDescriptionModal categoryServices={categoryServices} proceedToSave={hideModal} saveService={handleSaveService} />}
    </div>
  );
};

const ServicesDescriptionLoader = (props) => {
  const { categories, getCategories, getSalonOnlineSettings, isSettingsDataFetched } = props;

  const salonId = useCurrentSalonId();

  const {
    value: valueCategories,
    loading: isLoadingCategories,
    error: errorCategories,
  } = useAsync(async () => {
    // NOTE: don't try to re-fetch if already there, it may cause infinite re-renders because of parent re-render
    if (Array.isArray(categories)) return;
    return await getCategories(salonId);
  }, [categories, getCategories, salonId]);

  const {
    value: valueSettings,
    loading: isLoadingSettings,
    error: errorSettings,
  } = useAsync(async () => {
    // NOTE: don't try to re-fetch if already there, it may cause infinite re-renders because of parent re-render
    if (isSettingsDataFetched) return;
    return await getSalonOnlineSettings(salonId);
  }, [getSalonOnlineSettings, isSettingsDataFetched, salonId]);

  if (errorCategories) L.warn('ServicesDescriptionLoader()', { valueCategories, isLoadingCategories, errorCategories });
  if (errorSettings) L.warn('ServicesDescriptionLoader()', { valueSettings, isLoadingSettings, errorSettings });

  return <ServicesDescription {...props} />;
};

const mapStateToProps = (state) => ({
  isSettingsDataFetched: selectBookingIsSettingsDataFetched(state),
  categoryServices: selectBookingCategoryServices(state),
  categories: selectSalonServicesCategories(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      putService,
      getCategories,
      getSalonOnlineSettings,
    },
    dispatch,
  );

const ServicesDescriptionHoc = connect(mapStateToProps, mapDispatchToProps)(ServicesDescriptionLoader);

export default ServicesDescriptionHoc;
